import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    EmailDocumentCategoryResult,
    InsertableArchiveResult,
    CarbonCopyContactResult,
    CaseResult,
    RecordTypeResult,
} from '../../../services/api/dataServiceOutlookQuickSave';
import { IDocumentDetails } from '../../../@types/models';
import { useQuickSaveSubmit } from './useQuickSaveSubmit';
import { navigateToImportArchive } from '../utils/importArchiveNavigation';

export interface FormState {
    caseTitle: string;
    caseDesc: string;
    caseReno: number | null;
    caseCategory: EmailDocumentCategoryResult | null;
    archive: InsertableArchiveResult | null;
    ccContacts: CarbonCopyContactResult[];
    showRecordTypes: boolean;
    isSubmitting: boolean;
    submitSuccess: boolean;
    submitError: string;
    documentDetails: IDocumentDetails | null;
    isRestricted: boolean;
    updateTitle: (title: string) => void;
    updateDescription: (desc: string) => void;
    updateCaseReno: (reno: number | null) => void;
    updateCaseCategory: (category: EmailDocumentCategoryResult | null) => void;
    updateArchive: (arch: InsertableArchiveResult | null) => void;
    updateCcContacts: (contacts: CarbonCopyContactResult[]) => void;
    setIsRestricted: (isRestricted: boolean) => void;
    handleSubmit: () => void;
    resetForm: () => void;
}

export interface UseFormStateProps {
    selectedArchive?: InsertableArchiveResult | null;
    selectedCategory?: EmailDocumentCategoryResult | null;
    selectedCase?: CaseResult | null;
    selectedRecordType?: RecordTypeResult | null;
    selectedCopyTo?: CarbonCopyContactResult[];
    title?: string;
    caseConnection?: number; // Add this to determine if case is required
    showRecordTypes?: boolean; // Add this to determine if record type is required
}

export const useFormState = (props?: UseFormStateProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    // Handle import archive navigation
    const handleImportArchive = useCallback((documentDetails: IDocumentDetails) => {
        navigateToImportArchive(navigate, documentDetails);
    }, [navigate]);
    const [caseTitle, setCaseTitle] = useState<string>('');
    const [caseDesc, setCaseDesc] = useState<string>('');
    const [caseReno, setCaseReno] = useState<number | null>(null);
    const [caseCategory, setCaseCategory] = useState<EmailDocumentCategoryResult | null>(null);
    const [archive, setArchive] = useState<InsertableArchiveResult | null>(null);
    const [ccContacts, setCcContacts] = useState<CarbonCopyContactResult[]>([]);
    const [showRecordTypes, setShowRecordTypes] = useState<boolean>(false);
    const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
    const [submitError, setSubmitError] = useState<string>('');
    const [documentDetails, setDocumentDetails] = useState<IDocumentDetails | null>(null);
    const [isRestricted, setIsRestricted] = useState<boolean>(false);

    // Use props values for validation if provided, otherwise use local state
    const effectiveArchive = props?.selectedArchive ?? archive;
    const effectiveCategory = props?.selectedCategory ?? caseCategory;
    const effectiveCase = props?.selectedCase;
    const effectiveRecordType = props?.selectedRecordType;
    const effectiveTitle = props?.title ?? caseTitle;
    const effectiveCaseConnection = props?.caseConnection ?? 1; // Default to 1 if not provided
    const effectiveShowRecordTypes = props?.showRecordTypes ?? showRecordTypes;

    // Submit hook integration - requires proper data structure
    const submitHook = useQuickSaveSubmit({
        title: effectiveTitle,
        selectedArchive: effectiveArchive!,
        selectedCategory: effectiveCategory!,
        selectedCase: effectiveCase, // This would come from case selection
        selectedRecordType: effectiveRecordType,
        selectedCopyTo: props?.selectedCopyTo ?? ccContacts,
        caseValidation: null, // This would come from validation
        showRecordTypes: effectiveShowRecordTypes,
        onSaveSuccess: (responseDocumentDetails?: IDocumentDetails | null) => {
            setSubmitSuccess(true);
            setSubmitError('');
            setDocumentDetails(responseDocumentDetails || null);
        },
        onSaveError: (error: string) => {
            setSubmitError(error);
            setSubmitSuccess(false);
            setDocumentDetails(null);
        },
        onImportArchive: handleImportArchive
    });

    // Handle submit with validation
    const handleSubmit = useCallback(() => {
        // Case is only required when caseConnection is not 0
        const isCaseRequired = effectiveCaseConnection !== 0;
        const isRecordTypeRequired = effectiveShowRecordTypes;

        if (!effectiveTitle.trim() ||
            !effectiveArchive ||
            !effectiveCategory ||
            (isCaseRequired && !effectiveCase) ||
            (isRecordTypeRequired && !effectiveRecordType)) {
            setSubmitError(t('Please fill in all required fields'));
            return;
        }

        setSubmitError('');
        setSubmitSuccess(false);
        submitHook.handleSubmit();
    }, [effectiveArchive, effectiveCategory, effectiveCase, effectiveCaseConnection, effectiveRecordType, effectiveShowRecordTypes,
        effectiveTitle, submitHook, t]);

    const updateTitle = useCallback((title: string) => {
        setCaseTitle(title);
    }, []);

    const updateDescription = useCallback((desc: string) => {
        setCaseDesc(desc);
    }, []);

    const updateCaseReno = useCallback((reno: number | null) => {
        setCaseReno(reno);
    }, []);

    const updateCaseCategory = useCallback((category: EmailDocumentCategoryResult | null) => {
        setCaseCategory(category);
        setShowRecordTypes(!!category);
    }, []);

    const updateArchive = useCallback((arch: InsertableArchiveResult | null) => {
        setArchive(arch);
    }, []);

    const updateCcContacts = useCallback((contacts: CarbonCopyContactResult[]) => {
        setCcContacts(contacts);
    }, []);

    const resetForm = useCallback(() => {
        setCaseTitle('');
        setCaseDesc('');
        setCaseReno(null);
        setCaseCategory(null);
        setArchive(null);
        setCcContacts([]);
        setShowRecordTypes(false);
        setSubmitSuccess(false);
        setSubmitError('');
        setDocumentDetails(null);
        setIsRestricted(false);
    }, []);

    return {
        // State
        caseTitle,
        caseDesc,
        caseReno,
        caseCategory,
        archive,
        ccContacts,
        showRecordTypes,
        isSubmitting: submitHook.isSubmitting,
        submitSuccess,
        submitError,
        documentDetails,
        isRestricted,
        // Actions
        updateTitle,
        updateDescription,
        updateCaseReno,
        updateCaseCategory,
        updateArchive,
        updateCcContacts,
        setIsRestricted,
        handleSubmit,
        resetForm
    };
};
